import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import { useI18n } from "@/context/I18nContext";

const MODULE_META: { key: string; label: string; group: string; desc: string }[] = [
  { key: "control", label: "Manager Control", group: "Control", desc: "Shift Command, opening/closing, actions, reviews" },
  { key: "communicate", label: "Communications", group: "Communicate", desc: "Targeted messages, acknowledgements" },
  { key: "execute", label: "Tasks & Execution", group: "Execute", desc: "Campaigns, checklists, verification" },
  { key: "know_learn", label: "Knowledge & Learning", group: "Know & Learn", desc: "SOPs, courses, certifications" },
  { key: "workforce", label: "Workforce & Scheduling", group: "Workforce", desc: "Roster, availability, schedules" },
  { key: "time_attendance", label: "Time & Attendance", group: "Workforce", desc: "Punches, timesheets, exports" },
  { key: "labor_planning", label: "Labor Planning", group: "Workforce", desc: "Demand forecasts, labor plans" },
  { key: "assure", label: "Visits & Audits", group: "Assure", desc: "Store walks, findings, corrective actions" },
  { key: "support", label: "Help Desk", group: "Support", desc: "Tickets, routing, service targets" },
  { key: "intelligence", label: "Performance & Exceptions", group: "Intelligence", desc: "KPIs, reconciliation, Exception Center" },
  { key: "assist", label: "AI Assistant", group: "Assist", desc: "Governed, permission-scoped AI" },
  { key: "connect", label: "Integrations", group: "Connect", desc: "Imports, APIs, webhooks, connectors" },
];

export default function Modules() {
  const {
    t
  } = useI18n();

  const { hasPerm, refreshMe } = useAuth();
  const [modules, setModules] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canManage = hasPerm("modules.manage");

  useEffect(() => {
    api.get("/admin/modules").then((r) => setModules(r.data.modules)).finally(() => setLoading(false));
  }, []);

  const toggle = (key: string) => {
    if (!canManage) return;
    setModules((m) => ({ ...m, [key]: !m[key] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/modules", { modules });
      await refreshMe();
      toast.success("Module configuration saved");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;

  return (
    (<div>
      <PageHeader
        title="Modules"
        subtitle="All standard modules are included. Activate only what your organization uses — no paywalls, no data loss."
        testId="modules-header"
        actions={canManage ? <Button onClick={save} disabled={saving} data-testid="save-modules">{saving ? t("Saving…") : t("Save changes")}</Button> : undefined}
      />
      <div className="grid gap-3 md:grid-cols-2">
        {MODULE_META.map((m) => (
          <Card key={m.key} className="flex items-start justify-between gap-3 p-4" data-testid={`module-card-${m.key}`}>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-brand">{m.group}</div>
              <div className="font-display font-semibold">{m.label}</div>
              <p className="mt-0.5 text-sm text-muted-foreground">{m.desc}</p>
            </div>
            <Switch
              checked={!!modules[m.key]}
              onCheckedChange={() => toggle(m.key)}
              disabled={!canManage}
              data-testid={`module-switch-${m.key}`}
            />
          </Card>
        ))}
      </div>
    </div>)
  );
}
